/* eslint-disable @typescript-eslint/no-explicit-any */
import type { AxiosProgressEvent } from 'axios'
import axios from 'axios'
import _ from 'lodash'

const init = () => {
  console.log('init axios')
  axios.defaults.headers.common['Content-Type'] = 'application/json'
  axios.defaults.headers.common['x-api-key'] = import.meta.env.VITE_REACT_APP_API_KEY
  axios.defaults.headers.common.Accept = 'application/json'
  axios.defaults.baseURL = import.meta.env.VITE_REACT_APP_ENDPOINT
  return true
}

interface SitecoreOptionsListItemLabels {
  [key: string]: string
}

interface SitecoreOptionsListItem {
  identifier: string
  labels: SitecoreOptionsListItemLabels
}

export interface OptionList {
  identifier: string
  label: string
}

export interface APIUser {
  inspecId: string
  poiName: string
  poiType: string
  email: string
  address: string
  city: string
  postalCode: string
  country: string
  territory: string
  spokenLanguages: string[]
  token: string
}

const checkToken = async (token: string): Promise<APIUser | undefined> => {
  console.debug('[checkToken] token', token)
  try {
    const result = await axios.post('/v1/checkToken', { token })
    return _.get(result, 'data', undefined)
  } catch (e) {
    console.error('[checkToken] error', e)
    return undefined
  }
}

export interface APIAsset {
  id: string
  image: string
  title: string
  subject: string
  subjectIdentifier: string
  copyright: string
  author: string
  qualityIdentifier: string
  relateddInspecId: string
  otherProvider: string
  uploaderEmail: string
  uploaderName: string
  uploaderPosition: string
}

export interface GetAssetsResult {
  assets?: APIAsset[]
  error?: unknown
}
const getAssets = async (token: string, targetId: string): Promise<GetAssetsResult> => {
  try {
    const result = await axios.post(
      '/v1/siteCore/search',
      {
        targetId,
      },
      {
        headers: {
          'Michelin-Uploader': token,
        },
      }
    )
    if (result.status >= 200 && result.status < 300) {
      return { assets: _.get(result, 'data.items', []) }
    }
    return { error: result.statusText }
  } catch (e) {
    return { error: e }
  }
}

const deleteAsset = async (
  token: string,
  id: string,
  copyright: string,
  otherProvider: string,
  poiName: string
) => {
  try {
    const result = await axios.post(
      '/v1/siteCore/delete',
      {
        id,
        copyright,
        otherProvider,
        poiName,
      },
      {
        headers: {
          'Michelin-Uploader': token,
        },
      }
    )
    return [_.get(result, 'data') || {}]
  } catch (e) {
    return [undefined, e]
  }
}

// const toBase64 = (file) =>
//   new Promise((resolve, reject) => {
//     const reader = new FileReader()
//     reader.readAsDataURL(file)
//     reader.onload = () => resolve(reader.result)
//     reader.onerror = (error) => reject(error)
//   })
export interface UploadMetadata {
  subject: string
  quality: string
  relateddInspecId: string
  author: string
  provider: string
  assetId?: string
  otherProvider: string
}
export interface UploadParameters {
  file: any
  userId: string
  metadata: UploadMetadata
}
export interface APIFile {
  Id: string
  userId: string
  assetId: number
  status: number
  title: string
  subject: string
  quality: string
  relateddInspecId: string
  author: string
  provider: string
  otherProvider: string
  copyright: string
}
export interface UploadResult {
  file?: APIFile
  error?: string
}
const upload = async (
  token: string,
  /*{ file, userId:string, metadata }*/ input: UploadParameters,
  onUploadProgress: (progressEvent: AxiosProgressEvent) => void
): Promise<UploadResult> => {
  const { file, userId, metadata } = input
  const {
    // title,
    subject,
    quality,
    relateddInspecId,
    author,
    provider,
    assetId,
    otherProvider,
  } = metadata
  const formData = new FormData()
  formData.append('file', file)
  console.log('file', file.size)

  formData.append('filename', _.get(file, 'name'))

  formData.append('contentType', _.get(file, 'type'))

  formData.append('userId', userId)
  formData.append('subject', subject)
  formData.append('quality', quality)
  formData.append('relateddInspecId', relateddInspecId)
  formData.append('author', author)
  formData.append('provider', provider)
  formData.append('assetId', assetId || '')
  formData.append('otherProvider', otherProvider)

  try {
    const result = await axios.post('/v1/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Michelin-Uploader': token,
      },
      onUploadProgress,
    })
    console.log('upload', result)
    if (result.status >= 200 && result.status < 300) {
      return { file: result.data }
    }
    return { error: result.statusText }
  } catch (e) {
    console.error('api.upload', e)
    console.log('upload error', _.get(e, 'message', ''))
    return { error: _.get(e, 'message', '') }
  }
}
const DEFAULT_LANGUAGE = 'en-US'

const getSubjectOptionsList = async (
  user: APIUser,
  language: string
): Promise<{ identifier: string; label: string }[]> => {
  let options = []

  try {
    const result = await axios.get(
      `/v1/siteCore/${user.poiType.toLowerCase()}/getSubjectOptionsList`
    )
    if (result && result.data) {
      options = result.data.map((e: SitecoreOptionsListItem) => ({
        identifier: e.identifier,
        label: e.labels[language] || e.labels[DEFAULT_LANGUAGE],
      }))
    }
    return options
  } catch (e) {
    console.debug('[api.getSubjectOptionsList]', e)
    return []
  }
}

const validate = async (token: string, params: any) => {
  try {
    const result = await axios.post('/v1/process', params, {
      headers: {
        'Michelin-Uploader': token,
      },
    })
    return [result?.data || {}]
  } catch (e) {
    return [undefined, e]
  }
}

const changeLanguage = async (
  token: string,
  newLanguage: string,
  userId: string
): Promise<boolean> => {
  console.log('[changeLanguage] newLanguage', newLanguage, 'userId', userId)

  try {
    await axios.post(
      '/v1/changeLanguage',
      {
        newLanguage,
        userId,
      },
      {
        headers: {
          'Michelin-Uploader': token,
        },
      }
    )
    return true
  } catch (e) {
    console.error('[changeLanguage] ', e)
    return false
  }
}

export {
  changeLanguage,
  checkToken,
  deleteAsset,
  getAssets,
  getSubjectOptionsList,
  init,
  upload,
  validate,
}
